import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { comparisonEntities } from "@/lib/results";
import { divergenceRows, slotCost } from "@/lib/lineup";
import { computeScore, scoreMeta } from "@/lib/scoring";
import { fmtNumber, fmtScore } from "@/lib/format";
import { CoachAvatar, Crest } from "@/components/Crest";
import { Pitch } from "@/components/Pitch";
import { FORMATION_COORDS } from "@/data/constants";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ENTITY } from "@/data/constants";
import { ChevronDown, Trophy, Download } from "lucide-react";
import { toPng } from "html-to-image";

const COLORS = { you: ENTITY.you.color, coach: ENTITY.coach.color, ai: ENTITY.ai.color };

function useCountUp(target, dur = 1000) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = Date.now();
    const step = () => {
      const p = Math.min(1, (Date.now() - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
}

export default function ResultScreen() {
  const { fixtureId } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { dataset, state, getFixtureById, set } = useDemo();
  const fixture = getFixtureById(fixtureId);
  useEffect(() => { if (fixture) set("activeFixtureId", fixture.id); }, [fixture, set]);

  if (!fixture || !fixture.homeSquad) {
    return <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">Fixture not available.</div>;
  }

  const { language, scoringMode, resultVariant } = state;
  const home = dataset.clubsById[fixture.homeId];
  const away = dataset.clubsById[fixture.awayId];
  const squad = fixture.homeSquad;
  const squadById = Object.fromEntries(squad.map((p) => [p.id, p]));
  const ent = comparisonEntities(fixture, state.submissions[fixtureId]);
  const you = ent.you, coach = ent.coach, ai = ent.ai, optimal = ent.optimal;

  const marginCoach = Math.round((coach.lgd - you.lgd) * 10000) / 10000; // >0 => you beat coach
  const marginAi = Math.round((you.lgd - ai.lgd) * 10000) / 10000; // >0 => ai better

  const rows = divergenceRows(you.assignment, coach.assignment, ai.assignment)
    .map((r) => ({ ...r, cost: slotCost(r.slot, r.you, squad, fixture.optimalEGD, optimal.assignment) || 0 }))
    .sort((a, b) => b.cost - a.cost);

  const costliest = rows.find((r) => r.cost > 0 && r.you && optimal.assignment[r.slot]);

  const shared = { fixture, home, away, you, coach, ai, optimal, marginCoach, marginAi, squadById, rows, costliest, language, scoringMode, t };

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-5">
      {resultVariant === "A"
        ? <VariantA {...shared} />
        : <VariantB {...shared} nav={nav} fixtureId={fixtureId} />}
    </div>
  );
}

function verdictText(t, coachName, marginCoach, language) {
  const m = fmtNumber(Math.abs(marginCoach), language, 2);
  if (marginCoach > 0.0005) return { text: t("result.beatCoach", { coach: coachName, margin: m }), win: true };
  if (marginCoach < -0.0005) return { text: t("result.coachBeat", { coach: coachName, margin: m }), win: false };
  return { text: t("result.drewCoach", { coach: coachName }), win: null };
}

function EntityCards({ you, coach, ai, scoringMode, language, t }) {
  const meta = scoreMeta(scoringMode);
  const list = [
    { k: "you", lgd: you.lgd, egd: you.egd },
    { k: "coach", lgd: coach.lgd, egd: coach.egd },
    { k: "ai", lgd: ai.lgd, egd: ai.egd },
  ].map((e) => ({ ...e, score: computeScore(scoringMode, { lgd: e.lgd, actualGD: 0, coachLGD: coach.lgd }) }))
    .sort((a, b) => a.lgd - b.lgd);
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="entity-cards">
      {list.map((e, i) => (
        <div key={e.k} className="rounded-xl border p-3 text-center" style={{ borderColor: `${COLORS[e.k]}55`, background: `${COLORS[e.k]}0d` }}>
          <div className="text-[10px] uppercase tracking-widest font-display" style={{ color: COLORS[e.k] }}>{t(`common.${e.k}`)}</div>
          <div className="font-display text-2xl sm:text-3xl font-bold tabular mt-1" style={{ color: COLORS[e.k] }}>
            {fmtScore(scoringMode, e.score, language)}
          </div>
          <div className="text-[9px] text-neutral-500 mt-0.5">#{i + 1} · {t(meta.labelKey)}</div>
        </div>
      ))}
    </div>
  );
}

function VariantA(props) {
  const { home, coach, you, ai, optimal, marginCoach, marginAi, scoringMode, language, t, costliest, squadById, rows } = props;
  const v = verdictText(t, home.coachName, marginCoach, language);
  const big = useCountUp(Math.abs(marginCoach));
  const accent = v.win ? "#b6ff2e" : v.win === false ? "#ffb547" : "#9ca3af";

  return (
    <div className="space-y-6" data-testid="result-variant-a">
      <div className="rounded-3xl border border-neutral-800 bg-gradient-to-b from-neutral-900 to-neutral-950 p-6 sm:p-10 text-center relative overflow-hidden rise-in">
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 0%, ${accent}, transparent 60%)` }} />
        <div className="relative">
          <div className="flex items-center justify-center gap-3 mb-4">
            <CoachAvatar name={home.coachName} size={46} ring={COLORS.coach} />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{t("common.coach")}</div>
              <div className="font-display font-semibold">{home.coachName}</div>
            </div>
          </div>
          <div className="font-display text-6xl sm:text-8xl font-bold tabular leading-none" style={{ color: accent }} data-testid="hero-margin">
            {fmtNumber(big, language, 2)}
          </div>
          <h1 className="font-display text-2xl sm:text-4xl font-bold uppercase tracking-wide mt-3 max-w-2xl mx-auto" data-testid="verdict-headline">
            {v.text}
          </h1>
          <p className="text-neutral-400 mt-2 flex items-center justify-center gap-1.5">
            {marginAi <= 0.0005 ? <Trophy size={15} className="text-[#3fd7e5]" /> : null}
            {marginAi > 0.0005 ? t("result.vsAiLost", { margin: fmtNumber(marginAi, language, 2) })
              : marginAi < -0.0005 ? t("result.vsAiBeat", { margin: fmtNumber(-marginAi, language, 2) })
                : t("result.vsAiDrew")}
          </p>
        </div>
      </div>

      <EntityCards you={you} coach={coach} ai={ai} scoringMode={scoringMode} language={language} t={t} />

      <div className="rounded-2xl border border-neutral-800 bg-neutral-900/50 p-5 text-center">
        <p className="text-neutral-300">
          {t("result.explanation", {
            your: fmtNumber(you.egd, language, 2),
            best: fmtNumber(ai.egd, language, 2),
            lgd: fmtNumber(you.lgd, language, 2),
          })}
        </p>
      </div>

      <ShareCard {...props} accent={accent} verdict={v.text} prominent />

      <Collapsible>
        <CollapsibleTrigger data-testid="analysis-toggle" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-neutral-800 text-neutral-300 hover:bg-neutral-900 text-sm">
          {t("result.viewAnalysis")} <ChevronDown size={16} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          {costliest && (
            <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-4 mb-3 text-sm text-neutral-200" data-testid="costliest-a">
              {t("result.costliest", { player: squadById[costliest.you]?.surname, slot: costliest.slot, best: squadById[optimal.assignment[costliest.slot]]?.surname, cost: fmtNumber(costliest.cost, language, 2) })}
            </div>
          )}
          <DivergenceTable rows={rows} squadById={squadById} optimal={optimal} language={language} t={t} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function VariantB(props) {
  const { home, coach, you, ai, optimal, marginCoach, marginAi, scoringMode, language, t, costliest, squadById, rows, nav, fixtureId } = props;
  const { dataset, state } = useDemo();
  const profile = dataset.profiles[state.profileId];
  const seasonAvg = profile.history.length ? profile.history.reduce((s, h) => s + h.lgd, 0) / profile.history.length : 0;
  const v = verdictText(t, home.coachName, marginCoach, language);

  return (
    <div className="space-y-4" data-testid="result-variant-b">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{t("result.analysis")}</div>
          <h1 className="font-display text-2xl font-bold uppercase" data-testid="verdict-headline">{v.text}</h1>
        </div>
        <Crest club={home} size={40} />
      </div>

      {/* dense 3-col table */}
      <div className="grid grid-cols-3 gap-2" data-testid="result-b-columns">
        {[["you", you], ["coach", coach], ["ai", ai]].map(([k, e]) => (
          <div key={k} className="rounded-xl border p-3" style={{ borderColor: `${COLORS[k]}55` }}>
            <div className="text-[10px] uppercase tracking-widest font-display mb-2" style={{ color: COLORS[k] }}>{t(`common.${k}`)}</div>
            <Metric label="E[GD]" value={fmtNumber(e.egd, language, 2)} />
            <Metric label="LGD" value={fmtNumber(e.lgd, language, 2)} />
            <Metric label={t(scoreMeta(scoringMode).labelKey)} value={fmtScore(scoringMode, computeScore(scoringMode, { lgd: e.lgd, actualGD: 0, coachLGD: coach.lgd }), language)} />
            <Metric label={t("common.formation")} value={e.formation} />
          </div>
        ))}
      </div>

      {costliest && (
        <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-4 text-sm text-neutral-200" data-testid="costliest-b">
          <span className="font-display uppercase text-[11px] text-red-400 tracking-wide">{t("result.costliest").split(":")[0]}:</span>{" "}
          {t("result.costliest", { player: squadById[costliest.you]?.surname, slot: costliest.slot, best: squadById[optimal.assignment[costliest.slot]]?.surname, cost: fmtNumber(costliest.cost, language, 2) })}
        </div>
      )}

      <DivergenceTable rows={rows} squadById={squadById} optimal={optimal} language={language} t={t} sorted />

      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-center rounded-xl border border-neutral-800 bg-neutral-900/50 p-4">
        <div className="flex gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{t("result.thisResult")}</div>
            <div className="font-display text-2xl font-bold tabular text-[#b6ff2e]">{fmtNumber(you.lgd, language, 2)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{t("result.seasonAvg")}</div>
            <div className="font-display text-2xl font-bold tabular text-neutral-300">{fmtNumber(seasonAvg, language, 2)}</div>
          </div>
        </div>
      </div>

      <ShareCard {...props} accent={v.win ? "#b6ff2e" : "#ffb547"} verdict={v.text} />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-neutral-900 last:border-0">
      <span className="text-[11px] text-neutral-500">{label}</span>
      <span className="font-display font-semibold tabular text-sm">{value}</span>
    </div>
  );
}

function DivergenceTable({ rows, squadById, optimal, language, t, sorted }) {
  const visibleRows = useMemo(
    () => rows.filter((r) => r.you || optimal.assignment[r.slot]),
    [rows, optimal],
  );
  return (
    <div className="rounded-xl border border-neutral-800 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase text-neutral-500 border-b border-neutral-800">
            <th className="text-left px-3 py-2 font-display">{t("comparison.slot")}</th>
            <th className="text-left px-2 py-2 font-display" style={{ color: COLORS.you }}>{t("common.you")}</th>
            <th className="text-left px-2 py-2 font-display" style={{ color: COLORS.ai }}>{t("common.optimal")}</th>
            <th className="text-right px-3 py-2 font-display">{t("comparison.cost")}</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r) => (
            <tr key={r.slot} className="border-b border-neutral-900 last:border-0">
              <td className="px-3 py-2 font-display font-bold text-neutral-300">{r.slot}</td>
              <td className="px-2 py-2 text-neutral-300">{r.you ? squadById[r.you]?.surname : "—"}</td>
              <td className="px-2 py-2 text-neutral-300">{optimal.assignment[r.slot] ? squadById[optimal.assignment[r.slot]]?.surname : "—"}</td>
              <td className="px-3 py-2 text-right tabular font-display">
                {r.cost > 0 ? <span className="text-red-400">−{fmtNumber(r.cost, language, 2)}</span> : <span className="text-neutral-600">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShareCard({ home, away, you, coach, ai, marginCoach, language, t, accent, verdict, prominent }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0a0a0a", cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `bethecoach-${home.code}-${away.code}.png`;
      a.click();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("[BeTheCoach] Share image export failed:", e);
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="share-card"
      className={`rounded-2xl overflow-hidden border ${prominent ? "border-neutral-700" : "border-neutral-800"}`}>
      <div ref={cardRef} className="p-5 relative" style={{ background: `linear-gradient(135deg, ${home.primary}22, #0a0a0a 70%)` }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Crest club={home} size={30} />
            <span className="font-display font-bold uppercase text-sm">{home.code} v {away.code}</span>
          </div>
          <span className="font-display uppercase text-[10px] tracking-widest text-neutral-400">{t("common.appName")}</span>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 font-display">{t("result.shareTitle")}</div>
        <div className="font-display text-2xl sm:text-3xl font-bold uppercase mt-1 mb-4" style={{ color: accent }}>{verdict}</div>
        <div className="grid grid-cols-3 gap-2">
          {[["you", you.lgd], ["coach", coach.lgd], ["ai", ai.lgd]].map(([k, lgd]) => (
            <div key={k} className="rounded-lg bg-black/40 p-2 text-center">
              <div className="text-[9px] uppercase font-display" style={{ color: COLORS[k] }}>{t(`common.${k}`)}</div>
              <div className="font-display text-xl font-bold tabular" style={{ color: COLORS[k] }}>{fmtNumber(lgd, language, 2)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-950">
        <span className="text-[11px] text-neutral-500">{t("result.shareSub")}</span>
        <button data-testid="share-download-btn" onClick={download} disabled={busy}
          className="flex items-center gap-1.5 text-xs text-[#b6ff2e] disabled:opacity-50">
          <Download size={14} /> {t("result.downloadImage")}
        </button>
      </div>
    </div>
  );
}
